import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { AnalyticsChart } from "@/components/analytics/AnalyticsChart";
import { BrandButton } from "@/components/brand/brand-button";
import {
	BrandCard,
	BrandCardContent,
	BrandCardDescription,
	BrandCardHeader,
	BrandCardTitle,
} from "@/components/brand/brand-card";
import {
	DashboardContent,
	DashboardHeaderSection,
	DashboardLayout,
} from "@/components/dashboard/DashboardLayout";
import { DashboardPageHeader } from "@/components/dashboard/DashboardPageHeader";
import {
	PLATFORM_ICONS,
	type PlatformKey,
} from "@/components/platforms/platform-icons";
import {
	isAnalyticsEligibleProvider,
	PROVIDER_LABELS,
	PROVIDER_MAX_DAYS,
} from "@/lib/analytics";
import { cn } from "@/lib/utils";

type ChannelSummary = {
	id: string;
	connected: boolean;
	displayName?: string | null;
	externalUsername?: string | null;
	provider: string;
};

type AnalyticsSeries = {
	label: string;
	canonicalKey?: string;
	data: Array<{ date: string; total: number }>;
	percentageChange: number;
};

type AnalyticsResponse = {
	cached?: boolean;
	summary: Record<string, number | undefined>;
	series: AnalyticsSeries[];
	meta: {
		provider: string;
		requestedDays?: number;
		effectiveDays?: number;
		cachedAt: string;
		cacheTtlSeconds: number;
		scopeMissing?: boolean;
		missingScopes?: string[];
		available?: boolean;
	};
};

export const Route = createFileRoute("/dashboard/analytics")({
	component: AnalyticsPage,
});

function useEligibleChannels() {
	return useQuery({
		queryFn: async () => {
			const res = await fetch("/api/social/accounts", {
				headers: { Accept: "application/json" },
			});
			if (!res.ok) return [] as ChannelSummary[];
			const data = (await res.json()) as { accounts: ChannelSummary[] };
			return (data.accounts ?? []).filter(
				(channel) =>
					channel.connected && isAnalyticsEligibleProvider(channel.provider),
			);
		},
		queryKey: ["analytics", "eligible-channels"],
	});
}

function useChannelAnalytics(channelId: string | null, days: number) {
	return useQuery({
		enabled: Boolean(channelId),
		queryFn: async () => {
			const res = await fetch(
				`/api/social/analytics/channel/${encodeURIComponent(channelId ?? "")}?days=${days}`,
				{ headers: { Accept: "application/json" } },
			);
			if (!res.ok) throw new Error("Failed to load analytics");
			return (await res.json()) as AnalyticsResponse;
		},
		queryKey: ["analytics", "channel", channelId, days],
		staleTime: 1000 * 60 * 5,
	});
}

function AnalyticsPage() {
	const { data: channels, isLoading } = useEligibleChannels();
	const sortedChannels = useMemo(() => {
		return [...(channels ?? [])].sort((a, b) =>
			(a.displayName ?? a.provider).localeCompare(b.displayName ?? b.provider),
		);
	}, [channels]);
	const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
	const [days, setDays] = useState<number>(30);

	const activeChannel = useMemo(() => {
		if (selectedChannelId) {
			return sortedChannels.find((c) => c.id === selectedChannelId) ?? null;
		}
		return sortedChannels[0] ?? null;
	}, [selectedChannelId, sortedChannels]);

	const dayOptions = useMemo(() => {
		if (!activeChannel) return [7, 30];
		const max = PROVIDER_MAX_DAYS[activeChannel.provider] ?? 30;
		return [7, 30, 90].filter((value) => value <= max);
	}, [activeChannel]);

	const effectiveDays = dayOptions.includes(days) ? days : dayOptions[0];

	const { data: analytics, isLoading: isAnalyticsLoading, error } = useChannelAnalytics(
		activeChannel?.id ?? null,
		effectiveDays,
	);

	return (
		<DashboardLayout>
			<DashboardHeaderSection>
				<DashboardPageHeader
					description="Track follower growth, impressions, and engagement across your connected social channels."
					title="Analytics"
				/>
			</DashboardHeaderSection>
			<DashboardContent>
				{isLoading ? (
					<div className="flex items-center justify-center py-16">
						<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
					</div>
				) : sortedChannels.length === 0 ? (
					<EmptyState />
				) : (
					<div className="grid gap-6 lg:grid-cols-[260px_1fr]">
						<ChannelRail
							activeChannelId={activeChannel?.id ?? null}
							channels={sortedChannels}
							onSelect={setSelectedChannelId}
						/>
						<div className="flex flex-col gap-4">
							{activeChannel && (
								<>
									<div className="flex items-center justify-between gap-3">
										<div>
											<h2 className="text-base font-medium">
												{activeChannel.displayName ??
													activeChannel.externalUsername ??
													PROVIDER_LABELS[activeChannel.provider]}
											</h2>
											<p className="text-xs text-muted-foreground">
												{PROVIDER_LABELS[activeChannel.provider]}
											</p>
										</div>
										<div className="flex items-center gap-1">
											{dayOptions.map((option) => (
												<button
													className={cn(
														"rounded-md px-3 py-1 text-xs font-medium transition",
														effectiveDays === option
															? "bg-foreground text-background"
															: "bg-muted text-muted-foreground hover:bg-muted/80",
													)}
													key={option}
													onClick={() => setDays(option)}
													type="button"
												>
													{option} days
												</button>
											))}
										</div>
									</div>
									{analytics?.meta.available === false ? (
										<ComingSoonCard provider={activeChannel.provider} />
									) : analytics?.meta.scopeMissing ? (
										<ReconnectCard provider={activeChannel.provider} />
									) : isAnalyticsLoading ? (
										<div className="flex items-center justify-center py-16">
											<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
										</div>
									) : error ? (
										<BrandCard>
											<BrandCardContent className="py-6 text-sm text-muted-foreground">
												Could not load analytics. Try again later.
											</BrandCardContent>
										</BrandCard>
									) : analytics ? (
										<AnalyticsView analytics={analytics} />
									) : null}
								</>
							)}
						</div>
					</div>
				)}
			</DashboardContent>
		</DashboardLayout>
	);
}

function ChannelRail({
	activeChannelId,
	channels,
	onSelect,
}: {
	activeChannelId: string | null;
	channels: ChannelSummary[];
	onSelect: (id: string) => void;
}) {
	return (
		<div className="flex flex-col gap-1">
			{channels.map((channel) => {
				const isActive = channel.id === activeChannelId;
				const renderIcon =
					PLATFORM_ICONS[channel.provider as PlatformKey] ?? null;
				return (
					<button
						className={cn(
							"flex items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition",
							isActive
								? "bg-muted font-medium text-foreground"
								: "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
						)}
						key={channel.id}
						onClick={() => onSelect(channel.id)}
						type="button"
					>
						{renderIcon ? renderIcon("h-4 w-4 shrink-0") : null}
						<span className="min-w-0 flex-1 truncate">
							{channel.displayName ??
								channel.externalUsername ??
								PROVIDER_LABELS[channel.provider]}
						</span>
					</button>
				);
			})}
		</div>
	);
}

function AnalyticsView({ analytics }: { analytics: AnalyticsResponse }) {
	const summaryEntries = Object.entries(analytics.summary).filter(
		([, value]) => typeof value === "number",
	) as Array<[string, number]>;

	return (
		<div className="flex flex-col gap-4">
			{summaryEntries.length > 0 && (
				<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
					{summaryEntries.map(([key, value]) => (
						<SummaryCard
							key={key}
							label={prettifyKey(key)}
							value={value}
						/>
					))}
				</div>
			)}
			<div className="grid gap-4 md:grid-cols-2">
				{analytics.series.map((series, index) => (
					<BrandCard key={`${series.label}-${index}`}>
						<BrandCardHeader>
							<BrandCardTitle>{series.label}</BrandCardTitle>
							<BrandCardDescription>
								{series.percentageChange > 0
									? `+${series.percentageChange}% over period`
									: series.percentageChange < 0
										? `${series.percentageChange}% over period`
										: "No change over period"}
							</BrandCardDescription>
						</BrandCardHeader>
						<BrandCardContent className="text-foreground">
							<AnalyticsChart data={series.data} />
						</BrandCardContent>
					</BrandCard>
				))}
			</div>
			{analytics.meta.cachedAt && (
				<p className="text-xs text-muted-foreground">
					Updated {new Date(analytics.meta.cachedAt).toLocaleString()}
					{analytics.cached ? " (cached)" : ""}
				</p>
			)}
		</div>
	);
}

function SummaryCard({ label, value }: { label: string; value: number }) {
	return (
		<div className="rounded-md border border-border bg-card px-4 py-3">
			<div className="text-xs text-muted-foreground">{label}</div>
			<div className="text-2xl font-semibold tabular-nums">
				{formatNumber(value)}
			</div>
		</div>
	);
}

function ComingSoonCard({ provider }: { provider: string }): ReactNode {
	return (
		<BrandCard>
			<BrandCardHeader>
				<BrandCardTitle>Analytics coming soon</BrandCardTitle>
				<BrandCardDescription>
					Analytics for {PROVIDER_LABELS[provider] ?? provider} are pending
					platform approval and will appear here once available. Your posts
					continue to publish as normal.
				</BrandCardDescription>
			</BrandCardHeader>
		</BrandCard>
	);
}

function ReconnectCard({ provider }: { provider: string }): ReactNode {
	return (
		<BrandCard>
			<BrandCardHeader>
				<BrandCardTitle>Reconnect for analytics</BrandCardTitle>
				<BrandCardDescription>
					This {PROVIDER_LABELS[provider] ?? provider} connection was made
					before we added analytics scopes. Reconnect to grant the additional
					read permission needed to load insights.
				</BrandCardDescription>
			</BrandCardHeader>
			<BrandCardContent>
				<Link to="/dashboard/connect">
					<BrandButton>Reconnect channels</BrandButton>
				</Link>
			</BrandCardContent>
		</BrandCard>
	);
}

function EmptyState() {
	return (
		<BrandCard>
			<BrandCardHeader>
				<BrandCardTitle>No channels connected yet</BrandCardTitle>
				<BrandCardDescription>
					Connect a Facebook Page, Instagram Business account, YouTube channel,
					Pinterest, TikTok, or Threads account to start seeing analytics.
				</BrandCardDescription>
			</BrandCardHeader>
			<BrandCardContent>
				<Link to="/dashboard/connect">
					<BrandButton>Connect a channel</BrandButton>
				</Link>
			</BrandCardContent>
		</BrandCard>
	);
}

function prettifyKey(key: string): string {
	return key
		.split("_")
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");
}

function formatNumber(n: number): string {
	if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
	if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
	return String(Math.round(n));
}
